import PropTypes from 'prop-types';
import { Pagination } from 'react-bootstrap';
import React from 'react';

/**
 * Pagination with ellipsis: first, current ± 1, last.
 */
export default function SmartPagination({ page, pages, onPageChange }) {
    if (pages <= 1) return null;

    const items = [];
    const add = (num) => {
        items.push(
            <Pagination.Item
                key={num}
                active={num === page}
                onClick={() => onPageChange(num)}
            >
                {num}
            </Pagination.Item>
        );
    };

    // Always show page 1
    add(1);

    // Ellipsis before current range
    if (page > 3) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
    }

    // Pages around current
    for (
        let i = Math.max(2, page - 1);
        i <= Math.min(pages - 1, page + 1);
        i++
    ) {
        add(i);
    }

    // Ellipsis after current range
    if (page < pages - 2) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
    }

    // Always show last page
    if (pages > 1) {
        add(pages);
    }

    // Wrap the pagination list in a <nav> landmark so assistive technology can
    // jump to it and announce its purpose. React Bootstrap's <Pagination>
    // renders a bare <ul class="pagination"> with no surrounding landmark, so
    // without this wrapper screen-reader users get no "Pagination" navigation
    // region. aria-label names the landmark for the rotor/landmark list.
    return (
        <nav aria-label="Pagination">
            <Pagination className="justify-content-center mb-0">
                <Pagination.Prev
                    disabled={page === 1}
                    onClick={() => page > 1 && onPageChange(page - 1)}
                    aria-label="Previous page"
                />
                {items}
                <Pagination.Next
                    disabled={page === pages}
                    onClick={() => page < pages && onPageChange(page + 1)}
                    aria-label="Next page"
                />
            </Pagination>
        </nav>
    );
}

SmartPagination.propTypes = {
    page: PropTypes.number.isRequired,
    pages: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
};
